import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoPlayer from "@/components/feature/VideoPlayer";
import ShareButtons from "@/components/feature/ShareButtons";
import VideoCard from "@/components/base/VideoCard";
import { useFavorites } from "@/hooks/useFavorites";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import { formatCompact, formatDate, formatDuration, popularityLabel, relativeTime } from "@/utils/format";

export default function VideoDetailPage() {
  const { videoId } = useParams();
  const video = videos.find((v) => v.id === videoId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!video) return;
    const prev = document.title;
    document.title = `${video.title} - 由${video.streamerName}演播 - 主播视频`;
    let desc = document.querySelector('meta[name="description"]');
    let created = false;
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
      created = true;
    }
    const prevDesc = desc.getAttribute("content");
    desc.setAttribute("content", video.description.slice(0, 150));
    return () => {
      document.title = prev;
      if (desc) {
        if (created) desc.remove();
        else desc.setAttribute("content", prevDesc ?? "");
      }
    };
  }, [video]);

  const related = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.category === video.category && v.id !== video.id)
      .sort((a, b) => b.views - a.views)
      .slice(0, 4);
  }, [video]);

  const flash = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  if (!video) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-900 text-foreground-500">
          <i className="ri-video-off-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-50">视频不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-400">这条视频可能已被移除，去首页看看其他精彩内容吧。</p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const category = categories.find((c) => c.key === video.category);
  const fav = isFavorite(video.id);

  const onToggleFav = () => {
    const nowFav = toggleFavorite(video.id);
    flash(nowFav ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        {category && (
          <>
            <Link to={`/category/${category.key}`} className="transition-colors hover:text-primary-400">
              {category.name}视频
            </Link>
            <i className="ri-arrow-right-s-line" />
          </>
        )}
        <span className="line-clamp-1 text-foreground-200">{video.title}</span>
      </nav>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <VideoPlayer cover={video.cover} title={video.title} duration={video.duration} />

          <h1 className="mt-6 font-heading text-xl font-extrabold leading-snug text-foreground-50 md:text-2xl">
            {video.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-400">
            <span className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500/20 font-label text-sm font-bold text-primary-400">
                {video.streamerAvatar}
              </span>
              <span className="font-semibold text-foreground-200">{video.streamerName}</span>
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" /> {formatDate(video.publishedAt)}（{relativeTime(video.publishedAt)}）
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-timer-line" /> 时长 {formatDuration(video.duration)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-play-circle-line" /> {formatCompact(video.views)} 次播放
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-thumb-up-line" /> {formatCompact(video.likes)} 点赞
            </span>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onToggleFav}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                fav
                  ? "bg-primary-500 text-background-950 hover:bg-primary-400"
                  : "border border-background-700 bg-background-900 text-foreground-100 hover:border-primary-500 hover:text-primary-300"
              }`}
            >
              <i className={fav ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"} />
              {fav ? "已收藏" : "收藏"}
            </button>
            <ShareButtons title={video.title} />
          </div>
          <p className="mt-3 text-xs text-foreground-600">
            <i className="ri-information-line" /> 收藏仅保存在当前浏览器（演示用途），未登录状态同样可用。
          </p>

          <section className="mt-8 rounded-lg border border-background-800 bg-background-900 p-5 md:p-6">
            <h2 className="font-heading text-base font-bold text-foreground-50">视频简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-300">{video.description}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-secondary-500/10 px-3 py-1 text-xs font-medium text-secondary-300 transition-colors hover:bg-secondary-500/20"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-6 rounded-lg border border-background-800 bg-background-900 p-5 md:p-6">
            <h2 className="font-heading text-base font-bold text-foreground-50">相关推荐分类</h2>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {categories.map((c) => (
                <Link
                  key={c.key}
                  to={`/category/${c.key}`}
                  className="flex items-center gap-1.5 rounded-full border border-background-700 px-4 py-2 text-sm text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-300"
                >
                  <i className={`${c.icon} text-primary-500`} />
                  {c.name}视频
                </Link>
              ))}
            </div>
          </section>
        </div>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-800 bg-background-900 p-5">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500/20 font-label text-lg font-bold text-primary-400">
                {video.streamerAvatar}
              </span>
              <div>
                <div className="font-heading text-base font-bold text-foreground-50">{video.streamerName}</div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                  <span className="rounded-full bg-accent-500/10 px-2 py-0.5 font-medium text-accent-300">
                    {popularityLabel(video.popularity)}
                  </span>
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm text-foreground-400">
              该主播在{video.subcategory}方向持续更新，本期为精选录像，后续还会有更多相关内容上线。
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-md bg-background-950 p-3 text-center">
                <div className="font-label text-sm font-bold text-foreground-100">{video.subcategory}</div>
                <div className="mt-1 text-xs text-foreground-500">内容方向</div>
              </div>
              <div className="rounded-md bg-background-950 p-3 text-center">
                <div className="font-label text-sm font-bold text-foreground-100">{formatCompact(video.views)}</div>
                <div className="mt-1 text-xs text-foreground-500">播放量</div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-800 bg-background-900 p-5">
            <h2 className="mb-4 font-heading text-base font-bold text-foreground-50">同类推荐</h2>
            <div className="flex flex-col gap-3">
              {related.map((v) => (
                <VideoCard
                  key={v.id}
                  id={v.id}
                  title={v.title}
                  cover={v.cover}
                  streamerName={v.streamerName}
                  streamerAvatar={v.streamerAvatar}
                  duration={v.duration}
                  views={v.views}
                  description={v.description}
                  variant="list"
                />
              ))}
            </div>
          </div>
        </aside>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 rounded-md border border-background-700 bg-background-800 px-5 py-3 text-sm font-medium text-foreground-100">
          {toast}
        </div>
      )}
    </div>
  );
}